import type { SessionLiveViewSnapshot, TutorMissingSession } from "./types";

export interface PresentedSubstitute {
  id: string;
  name: string;
  initials: string;
  gradeSubjectMatch: string;
  freeUntilLabel: string;
}

export interface PresentedTutorMissingSession {
  id: string;
  studentName: string;
  initials: string;
  gradeSubjectLabel: string;
  startedAgoLabel: string;
  windowLabel: string;
  studentJoinedLabel: string;
  remainingWindowLabel: string;
  approvalLabel: string;
  assignedTutorName: string | null;
  substitutes: PresentedSubstitute[];
}

export interface PresentedSessionLiveView {
  scheduledToday: number;
  completed: number;
  liveNow: number;
  upcomingToday: number;
  bothJoined: number;
  tutorMissing: number;
  studentMissing: number;
  neitherJoined: number;
  tutorMissingSessions: PresentedTutorMissingSession[];
}

function formatClock(iso: string, timeZone: string): string {
  const formatted = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(new Date(iso));
  return formatted.replace(/\s/g, "").toLowerCase();
}

function formatWindow(start: string, end: string, timeZone: string): string {
  return `${formatClock(start, timeZone)}–${formatClock(end, timeZone)}`;
}

function startedAgoLabel(startedAt: string, asOf: string): string {
  const minutes = Math.max(0, Math.round((new Date(asOf).getTime() - new Date(startedAt).getTime()) / 60000));
  return minutes === 1 ? "Started 1 min ago" : `Started ${minutes} min ago`;
}

function presentSession(session: TutorMissingSession, snapshot: SessionLiveViewSnapshot): PresentedTutorMissingSession {
  const assigned = session.substitutes.find((tutor) => tutor.id === session.assignedTutorId) ?? null;
  return {
    id: session.id,
    studentName: session.studentName,
    initials: session.initials,
    gradeSubjectLabel: session.gradeSubjectLabel,
    startedAgoLabel: startedAgoLabel(session.startedAt, snapshot.asOf),
    windowLabel: formatWindow(session.windowStart, session.windowEnd, snapshot.timeZone),
    studentJoinedLabel: `Student joined ${formatClock(session.studentJoinedAt, snapshot.timeZone)}`,
    remainingWindowLabel: formatWindow(snapshot.asOf, session.windowEnd, snapshot.timeZone),
    approvalLabel: session.approvalLabel,
    assignedTutorName: assigned?.name ?? null,
    substitutes: session.substitutes.map((tutor) => ({
      id: tutor.id,
      name: tutor.name,
      initials: tutor.initials,
      gradeSubjectMatch: tutor.gradeSubjectMatch,
      freeUntilLabel: `Free until ${formatClock(tutor.freeUntil, snapshot.timeZone)}`,
    })),
  };
}

export function presentSessionLiveView(snapshot: SessionLiveViewSnapshot): PresentedSessionLiveView {
  const tutorMissing = snapshot.tutorMissingSessions.length;
  return {
    scheduledToday: snapshot.scheduledToday,
    completed: snapshot.completed,
    upcomingToday: snapshot.upcomingToday,
    bothJoined: snapshot.bothJoined,
    tutorMissing,
    studentMissing: snapshot.studentMissing,
    neitherJoined: snapshot.neitherJoined,
    liveNow: snapshot.bothJoined + tutorMissing + snapshot.studentMissing + snapshot.neitherJoined,
    tutorMissingSessions: snapshot.tutorMissingSessions.map((session) => presentSession(session, snapshot)),
  };
}
